import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { subjects, type AuthUser } from "@facturar/auth";
import {
  authClient,
  REDIRECT_URI,
  clearTokens,
  loadTokens,
  pushPostLoginRedirect,
  saveChallenge,
  saveTokens,
} from "@/lib/auth-client";
import { setOnUnauthorized } from "@/lib/api";

type AuthState =
  | { status: "loading"; user: null }
  | { status: "unauthenticated"; user: null }
  | { status: "authenticated"; user: AuthUser }
  | { status: "forbidden"; user: AuthUser };

type AuthContextValue = AuthState & {
  signIn: (returnTo?: string) => Promise<void>;
  signOut: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    status: "loading",
    user: null,
  });
  const signInInFlightRef = useRef(false);

  const applyVerifiedUser = useCallback((user: AuthUser) => {
    if (user.role !== "admin") {
      clearTokens();
      setState({ status: "forbidden", user });
      return;
    }
    setState({ status: "authenticated", user });
  }, []);

  useEffect(() => {
    let cancelled = false;
    const tokens = loadTokens();
    if (!tokens) {
      setState({ status: "unauthenticated", user: null });
      return;
    }
    void (async () => {
      try {
        const verified = await authClient.verify(subjects, tokens.access, {
          refresh: tokens.refresh || undefined,
        });
        if (cancelled) return;
        if (verified.err) {
          clearTokens();
          setState({ status: "unauthenticated", user: null });
          return;
        }
        if (verified.tokens?.refresh) {
          saveTokens({
            access: verified.tokens.access,
            refresh: verified.tokens.refresh,
          });
        } else if (verified.tokens?.access) {
          saveTokens({
            access: verified.tokens.access,
            refresh: tokens.refresh,
          });
        }
        applyVerifiedUser(verified.subject.properties as AuthUser);
      } catch {
        if (cancelled) return;
        clearTokens();
        setState({ status: "unauthenticated", user: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applyVerifiedUser]);

  const signIn = useCallback(async (returnTo?: string) => {
    if (signInInFlightRef.current) return;
    signInInFlightRef.current = true;
    if (returnTo) pushPostLoginRedirect(returnTo);
    const { challenge, url } = await authClient.authorize(REDIRECT_URI, "code", {
      pkce: true,
      provider: "code",
    });
    saveChallenge(challenge);
    window.location.href = url;
  }, []);

  const signOut = useCallback(() => {
    clearTokens();
    setState({ status: "unauthenticated", user: null });
    window.location.assign("/login");
  }, []);

  useEffect(() => {
    setOnUnauthorized(() => {
      const path = window.location.pathname;
      if (path.startsWith("/auth/") || path === "/login") return;
      clearTokens();
      void signIn(path + window.location.search);
    });
    return () => setOnUnauthorized(null);
  }, [signIn]);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, signIn, signOut }),
    [state, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

export function useAuthAdmin(): AuthUser {
  const auth = useAuth();
  if (auth.status !== "authenticated") {
    throw new Error("useAuthAdmin called outside authenticated admin context");
  }
  return auth.user;
}